import { useCallback, useEffect, useState } from "react";
import { FileText, Image as ImageIcon, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BlogAutoCard } from "@/components/BlogAutoCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = { title: "", excerpt: "", body: "", tags: [], cover_url: "", status: "draft" };
const field = "w-full rounded-lg border border-ink-600 bg-ink-900 p-3 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-gold/60";

/** One editor for both blogs: the platform's own, and an operator's. Claude writes the first
 *  draft from a topic; a person edits it and decides when it goes live. */
export const BlogStudio = ({ scope = "operator", publicBase = "" }) => {
  const root = scope === "admin" ? "/admin/blog" : "/operator/blog";
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [writer, setWriter] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${root}/posts`);
      setPosts(data.posts);
    } catch (e) { setError(errMsg(e)); }
  }, [root]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/ai/models").then(({ data }) => setWriter(data.labels?.writer || "")).catch(() => {});
  }, []);

  const open = async (card) => {
    setError(""); setNotice("");
    const { data } = await api.get(`${root}/posts`);
    const full = data.posts.find((p) => p.id === card.id);
    setPost({ ...EMPTY, ...card, ...full, body: card.body ?? full?.body ?? "" });
    if (!card.body) {
      // The list omits bodies — fetch the published copy when there is one, else keep what we have.
      try {
        const path = scope === "admin" ? `/public/blog/${card.slug}`
          : `/public/company/${publicBase}/blog/${card.slug}`;
        const { data: one } = await api.get(path);
        setPost((p) => ({ ...p, body: one.post.body }));
      } catch { /* a draft has no public copy — the editor opens empty and saves fine */ }
    }
  };

  const draft = async () => {
    if (!topic.trim()) return;
    setBusy("draft"); setError(""); setNotice("");
    try {
      const { data } = await api.post(`${root}/draft`, { topic: topic.trim() });
      setPost({ ...EMPTY, ...data.draft });
      if (data.model) setWriter(data.model);
      setNotice(`Draft written by ${data.model || "the AI"}. Read it before you publish.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    if (!post?.title?.trim()) { setError("A post needs a title."); return; }
    setBusy("save"); setError(""); setNotice("");
    const payload = { title: post.title, excerpt: post.excerpt || "", body: post.body || "",
                      tags: post.tags || [], cover_url: post.cover_url || "",
                      status: post.status || "draft" };
    try {
      const { data } = post.id
        ? await api.put(`${root}/posts/${post.id}`, payload)
        : await api.post(`${root}/posts`, payload);
      setPost({ ...post, ...data.post });
      setNotice(payload.status === "published" ? "Published." : "Saved as a draft.");
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const cover = async () => {
    if (!post?.title?.trim()) {
      setError("Give it a title first — the picture is shot from the title.");
      return;
    }
    setBusy("cover"); setError(""); setNotice("");
    try {
      const { data } = await api.post(`${root}/cover`,
        { title: post.title, excerpt: post.excerpt || "" });
      setPost((p) => ({ ...p, cover_url: data.cover_url }));
      setNotice("New cover photo. Save the post to keep it.");
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const remove = async () => {
    setBusy("delete");
    try {
      await api.delete(`${root}/posts/${post.id}`);
      setPost(null);
      setNotice("Post deleted.");
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="blog-studio" className="grid gap-5 lg:grid-cols-[18rem_1fr]">
      <div className="min-w-0 space-y-3">
        <Button data-testid="blog-new" onClick={() => { setPost({ ...EMPTY }); setNotice(""); }}
          className="w-full bg-gold text-ink-900 hover:bg-gold-light">
          <Plus className="mr-2 h-4 w-4" /> New post
        </Button>
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            Draft with AI
          </p>
          {writer && (
            <p data-testid="blog-writer-model" className="mt-1 font-mono text-[10px] uppercase tracking-widest text-gold/70">
              {writer}
            </p>
          )}
          <Input data-testid="blog-topic" value={topic} className="mt-2"
            placeholder="How early to leave for JFK" onChange={(e) => setTopic(e.target.value)} />
          <Button data-testid="blog-draft" variant="outline" className="mt-2 w-full"
            disabled={busy === "draft"} onClick={draft}>
            {busy === "draft" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              : <Sparkles className="mr-2 h-4 w-4" />}
            Write a draft
          </Button>
        </div>
        {scope === "admin" && <BlogAutoCard onWritten={load} />}
        <ul data-testid="blog-list" className="space-y-1">          {posts.map((p) => (
            <li key={p.id}>
              <button data-testid={`blog-post-${p.slug}`} onClick={() => open(p)}
                className={`w-full rounded-lg px-3 py-2 text-left transition-colors duration-200 ${
                  post?.id === p.id ? "bg-gold/10 text-gold" : "text-slate-300 hover:bg-ink-800"}`}>
                <span className="block truncate text-sm">{p.title}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {p.status}
                </span>
              </button>
            </li>
          ))}
          {posts.length === 0 && (
            <li data-testid="blog-empty" className="px-3 py-2 text-sm text-slate-500">
              Nothing written yet.
            </li>
          )}
        </ul>
      </div>

      {!post ? (
        <div className="grid min-w-0 place-items-center rounded-xl border border-dashed border-ink-600 p-12 text-center">
          <div>
            <FileText className="mx-auto h-6 w-6 text-slate-600" />
            <p className="mt-3 text-sm text-slate-500">
              Pick a post, or give the AI a topic and it writes the first draft.
            </p>
          </div>
        </div>
      ) : (
        <div className="min-w-0 space-y-3 rounded-xl border border-ink-600 bg-ink-800 p-5">
          <Input data-testid="blog-title" value={post.title} placeholder="Title"
            onChange={(e) => setPost({ ...post, title: e.target.value })} />
          <textarea data-testid="blog-excerpt" value={post.excerpt} rows={2} className={field}
            placeholder="One line that shows on the list page"
            onChange={(e) => setPost({ ...post, excerpt: e.target.value })} />
          <textarea data-testid="blog-body" value={post.body} rows={18} className={`${field} font-mono text-[13px]`}
            placeholder="Markdown body — ## subheadings, - bullets"
            onChange={(e) => setPost({ ...post, body: e.target.value })} />
          <Input data-testid="blog-tags" value={(post.tags || []).join(", ")} placeholder="Tags, comma separated"
            onChange={(e) => setPost({ ...post, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />
          <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Cover photo
              </p>
              <Button data-testid="blog-cover" size="sm" variant="outline" disabled={busy === "cover"}
                onClick={cover}>
                {busy === "cover" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  : <ImageIcon className="mr-2 h-3.5 w-3.5" />}
                {post.cover_url ? "Make a different one" : "Make a cover"}
              </Button>
            </div>
            {post.cover_url ? (
              <img data-testid="blog-cover-preview" src={post.cover_url} alt="Cover"
                className="mt-3 aspect-[16/9] w-full rounded-lg object-cover" />
            ) : (
              <p className="mt-2 text-xs text-slate-500">
                A picture makes the card on your site look finished. The AI shoots one from the
                title — it takes a few seconds.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="blog-status" value={post.status}
              onChange={(e) => setPost({ ...post, status: e.target.value })}
              className="h-10 rounded-lg border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <Button data-testid="blog-save" disabled={busy === "save"} onClick={save}
              className="bg-gold text-ink-900 hover:bg-gold-light">
              {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
            {post.id && (
              <Button data-testid="blog-delete" variant="ghost" disabled={busy === "delete"}
                onClick={remove}>
                <Trash2 className="h-4 w-4 text-slate-500" />
              </Button>
            )}
            {post.slug && post.status === "published" && (
              <a data-testid="blog-view" target="_blank" rel="noreferrer"
                href={scope === "admin" ? `/blog/${post.slug}` : `/book/${publicBase}/blog/${post.slug}`}
                className="text-sm text-gold hover:underline">View it live</a>
            )}
          </div>
          {notice && <p data-testid="blog-notice" className="text-sm text-gold">{notice}</p>}
          {error && <p data-testid="blog-error" className="text-sm text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
};
